<template>
  <div
    ref="rootEl"
    v-click-outside="closeAutocomplete"
    class="autocomplete"
    :class="{ 'is-bordered': bordered }"
  >
    <div ref="inputContainer" class="autocomplete-input-container">
      <input
        ref="searchField"
        v-model="searchString"
        class="autocomplete-input input"
        :class="sizeClass"
        type="text"
        :placeholder="placeholder"
        autocomplete="off"
        data-1p-ignore
        data-lpignore="true"
        data-form-type="other"
        @input="updateSearch"
        @keyup.esc.exact="clear"
        @keydown.down.prevent="moveDown"
        @keydown.up.prevent="moveUp"
        @keydown.tab="setSearchToSelected"
        @keyup.enter.exact="setSearchToSelected"
        @keydown.delete="onBackspace"
      />
      <slot></slot>
    </div>
    <Teleport v-if="showDropdown" :to="teleportTarget">
      <ul
        ref="resultsDropdown"
        class="autocomplete-results p-1 r-2 dropdown-content"
        :class="sizeClass"
        :style="resultsStyle"
      >
        <li
          v-for="(result, index) in results"
          :key="result[0]"
          class="autocomplete-result r-1"
          :class="{ 'is-active': index === resultIndex }"
          @click="setSearchResult(result)"
          @mouseover="resultIndex = index"
        >
          <slot
            name="suggestion"
            :result="result"
            :index="index"
            :is-active="index === resultIndex"
          >
            {{ result[1] }}
          </slot>
        </li>
        <li v-if="isEmptyWithQuery" class="autocomplete-result r-1">
          <slot name="empty" :query="searchString.trim()" />
        </li>
      </ul>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
// a flexible autocomplete input
//
// The parent component should manage available results by listening for 'search'
// events and updating the 'results' prop as needed.
//
// In addition to props/emits, this component makes the 'focus' and 'clear' methods
// available via defineExpose for use by parent components.

import { computed, onBeforeMount, onBeforeUnmount, onMounted, ref, useSlots, watch } from "vue"
import { debounce as _debounce } from "lodash-es"

interface Props {
  results: Array<[any, string]>
  debounce?: number
  /**
   * Minimum trimmed query length before a `search` is emitted. Guards against
   * broad, expensive fetches on 1–2 char queries. Below the threshold the
   * component emits an empty query so the parent clears stale results. `0`
   * disables the guard (any non-empty query searches).
   */
  minLength?: number
  initialValue?: string
  placeholder?: string
  size?: string
  /**
   * When true, the input keeps its `.input` chrome (border, padding, height).
   * Default `false` preserves the embedded look used inside chip-input rows.
   */
  bordered?: boolean
  /**
   * When true, the search box clears after a selection instead of keeping the
   * chosen label. Chip-style pickers (junction / reference / tag inputs) want
   * this — the selection becomes a chip and the box should reset for the next
   * search. Single-value pickers (e.g. RecordAutocomplete) leave it `false` so
   * the box keeps showing the selected label.
   */
  clearOnSelect?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  debounce: 150,
  minLength: 3,
  initialValue: "",
  placeholder: "",
  size: "normal",
  bordered: false,
  clearOnSelect: false,
})

const emit = defineEmits<{
  search: [query: string]
  select: [result: [any, string]]
  /** Backspace pressed while the search field is empty. Parents that
   *  render chips ahead of this input use it to remove the last one. */
  removeLast: []
}>()

const slots = useSlots()

const rootEl = ref<HTMLElement>()
const inputContainer = ref<HTMLElement>()
const searchField = ref<HTMLInputElement>()
const resultsDropdown = ref<HTMLElement>()

const resultIndex = ref(-1)
const searchString = ref("")
const showResults = ref(false)
const showDropdown = ref(false)
const dropdownPosition = ref({ top: 0, left: 0, width: 0 })
let scrollHandler: (() => void) | null = null
const modalContainer = ref<HTMLElement | null>(null)

const activeResult = computed(() => {
  if (resultIndex.value === -1) return null
  return props.results[resultIndex.value]
})

const teleportTarget = computed(() => modalContainer.value || "body")

const hasEmptySlot = computed(() => !!slots["empty"])

const isEmptyWithQuery = computed(
  () =>
    hasEmptySlot.value &&
    !props.results.length &&
    searchString.value.trim().length >= Math.max(props.minLength, 1)
)

const resultsStyle = computed(() => {
  const max = 16
  let height: number | undefined
  if (props.results?.length) {
    height = Math.min(props.results.length * 2.5, max)
  }

  return {
    position: modalContainer.value ? ("absolute" as const) : ("fixed" as const),
    top: `${dropdownPosition.value.top}px`,
    left: `${dropdownPosition.value.left}px`,
    width: `${dropdownPosition.value.width}px`,
    height: height ? `${height}rem` : "auto",
  }
})

const sizeClass = computed(() => "is-" + props.size)

// Create debounced or direct search function
let updateSearch: () => void
const _updateSearch = () => {
  const q = searchString.value.trim()
  // Below the minimum length, emit an empty query so the parent clears any
  // stale results rather than firing a fetch for too-short input.
  emit("search", q.length >= props.minLength ? q : "")
}

if (props.debounce) {
  updateSearch = _debounce(_updateSearch, props.debounce)
} else {
  updateSearch = _updateSearch
}

// Create bound scroll handler
scrollHandler = () => updateDropdownPosition()

watch(
  () => props.initialValue,
  () => {
    searchString.value = props.initialValue
  }
)

watch(
  () => props.results,
  (newValue, oldValue) => {
    showResults.value = !!newValue.length
    showDropdown.value = showResults.value || isEmptyWithQuery.value

    if (showDropdown.value) {
      updateDropdownPosition()
    }

    if (resultIndex.value > -1 && oldValue && resultIndex.value < oldValue.length) {
      const oldSelected = oldValue[resultIndex.value]!
      const newSelected = newValue.findIndex((item) => item[0] === oldSelected[0])
      if (newSelected > -1) {
        resultIndex.value = newSelected
      }
      return
    }

    resultIndex.value = newValue?.length ? 0 : -1
  },
  { deep: true }
)

watch(showDropdown, (newValue) => {
  if (newValue) {
    updateDropdownPosition()
    addScrollListeners()
  } else {
    removeScrollListeners()
  }
})

onBeforeMount(() => {
  searchString.value = props.initialValue
})

onMounted(() => {
  detectModalContainer()
})

onBeforeUnmount(() => {
  removeScrollListeners()
})

function detectModalContainer() {
  let element = rootEl.value as HTMLElement | undefined
  while (element) {
    if (element.classList?.contains("modal-content")) {
      modalContainer.value = element
      return
    }
    element = element.parentElement as HTMLElement | undefined
  }
  modalContainer.value = null
}

function updateDropdownPosition() {
  const container = inputContainer.value
  if (!container) return

  const rect = container.getBoundingClientRect()

  if (modalContainer.value) {
    const modalRect = modalContainer.value.getBoundingClientRect()
    dropdownPosition.value = {
      top: rect.bottom - modalRect.top + modalContainer.value.scrollTop + 2,
      left: rect.left - modalRect.left + modalContainer.value.scrollLeft - 3,
      width: Math.max(rect.width, 200) + 6,
    }
  } else {
    dropdownPosition.value = {
      top: rect.bottom + 2,
      left: rect.left - 3,
      width: Math.max(rect.width, 200) + 6,
    }
  }
}

function addScrollListeners() {
  let element: HTMLElement | null = inputContainer.value || null
  while (element) {
    element.addEventListener("scroll", scrollHandler!, { passive: true })
    element = element.parentElement
  }
  window.addEventListener("scroll", scrollHandler!, { passive: true })
  window.addEventListener("resize", scrollHandler!, { passive: true })
}

function removeScrollListeners() {
  let element: HTMLElement | null = inputContainer.value || null
  while (element) {
    element.removeEventListener("scroll", scrollHandler!)
    element = element.parentElement
  }
  window.removeEventListener("scroll", scrollHandler!)
  window.removeEventListener("resize", scrollHandler!)
}

function clear() {
  searchString.value = ""
  updateSearch()
  showResults.value = false
  showDropdown.value = false
  resultIndex.value = -1
}

function closeAutocomplete(event?: MouseEvent) {
  if (resultsDropdown.value?.contains(event?.target as Node)) {
    return
  }
  showResults.value = false
  showDropdown.value = false
  resultIndex.value = -1
}

function focus(selectAll = false) {
  searchField.value?.focus()
  // Callers editing an existing value (e.g. single-value ReferenceInput) select
  // the text so one keystroke replaces it or Backspace clears it.
  if (selectAll) searchField.value?.select()
}

function moveDown() {
  if (showResults.value && resultIndex.value < props.results.length) {
    resultIndex.value = resultIndex.value + 1
  }
}

function moveUp() {
  if (showResults.value && resultIndex.value > 0) {
    resultIndex.value = resultIndex.value - 1
  }
}

function setSearchResult(result: [any, string]) {
  // Chip pickers reset the box (clearOnSelect); single-value pickers keep the
  // chosen label so the input reflects the current selection.
  searchString.value = props.clearOnSelect ? "" : result[1]
  emit("select", result)
  closeAutocomplete()
}

function setSearchToSelected() {
  if (activeResult.value) {
    setSearchResult(activeResult.value)
  }
}

function onBackspace(event: KeyboardEvent) {
  // Only when the field is empty — otherwise let backspace edit the text.
  if (event.key !== "Backspace" || searchString.value !== "") return
  emit("removeLast")
}

defineExpose({ focus, clear })
</script>

<style>
.autocomplete {
  position: relative;
  width: 100%;

  input {
    padding: 0 calc(var(--auger-control-padding-x) / 2);
    margin: 0;
    border: none;
    box-shadow: none !important;
    border-radius: 0 !important;
    background: transparent;
    width: 100%;
    font-size: inherit;
    /* Embedded in a chip-input row, the input sits beside fixed-height chips.
       A bare input is only as tall as its line-box, which differs from the
       chip height — so the row grows when empty (just the input) and shrinks
       once a chip resolves. Pin the input to the chip height for its size so
       the row clamps to its `min-height` in both states. The `.tag` heights
       (components.css) are the canonical chip heights. */
    height: 2em;
    line-height: 2em;

    height: calc(var(--auger-control-height) - (var(--auger-control-padding-y) * 2));
  }

  /* The canonical small chip, not 2em — a small chip is 22.2px, so a 24px
     search box stood 1.8px proud of the chips it sits beside and pushed a
     multi-value row past the single-value one. */
  &.is-small input {
    height: var(--auger-chip-height-small);
    line-height: var(--auger-chip-height-small);
    padding-left: 2px;
  }

  &.is-large input {
    height: var(--auger-control-height-large);
    line-height: var(--auger-control-height-large);
  }

  /* Standalone use: re-enable the .input chrome that the embedded reset above
     strips. Mirrors the base `.input` rule in components.css so the field
     matches sibling form inputs (border, padding, height, focus ring). */
  &.is-bordered input.autocomplete-input {
    padding: var(--auger-control-padding-y) var(--auger-control-padding-x);
    border: 1px solid var(--auger-input-border);
    border-radius: var(--auger-radius) !important;
    font-size: var(--auger-control-size);
    color: var(--auger-text);
    background: var(--auger-bg);
    box-shadow: var(--auger-input-shadow) !important;
    height: var(--auger-control-height);
    line-height: normal;
  }

  &.is-bordered input.autocomplete-input:focus {
    border-color: var(--auger-primary);
    box-shadow: 0 0 0 2px var(--auger-focus-ring) !important;
  }
}

.autocomplete-results {
  text-align: left;
  /* position is set dynamically via resultsStyle (fixed for body, absolute for modal) */
  padding: 0;
  margin: 0 !important;
  height: 12rem;
  overflow-x: auto;
  overflow-y: scroll;
  background: var(--auger-bg);
  z-index: 10000;

  &.is-normal {
    font-size: var(--auger-control-size);
  }

  &.is-small {
    font-size: var(--auger-size-small);
  }
}

.autocomplete-result {
  padding: 4px 6px;
  cursor: pointer;
  opacity: 0.8;
  overflow: hidden;

  &.is-active {
    background: var(--auger-bg-muted);
    opacity: 1;
  }
}

input.autocomplete-input:focus {
  box-shadow: inherit;
}
</style>
